import { AccountTypes } from '@onevesthq/ov-enums';
import { t } from 'i18next';
import { isNumber } from 'lodash-es';

export type StateTaxConfig = {
  minTaxWithholding: number;
  defaultTaxWithholding: (
    federalTaxInput: number,
    isWithdrawAll: boolean,
  ) => number;
  canChangeTaxWithholding: (federalTaxInput: number) => boolean;
  isValidTaxWithholding?: (
    stateTaxInput: number,
    federalTaxInput: number,
    isWithdrawAll?: boolean,
  ) => boolean;
  stateTaxInfoMessage?: (federalTaxInput: number) => string;
  inlineErrorMessage?: (
    federalTaxInput: number,
    isWithdrawAll: boolean,
  ) => string;
};

export type StateTaxConfigs = {
  [state: string]: StateTaxConfig;
};

export type FederalTaxConfig = {
  defaultTaxWithholding: number;
  canChangeTaxWithholding: boolean;
};

export type TaxConfigInput = {
  accountType: AccountTypes;
};

export type TaxConfigResponse = {
  federalTaxConfig: FederalTaxConfig;
  stateTaxConfig: StateTaxConfigs;
};

const buildFederalTaxConfig = (input: TaxConfigInput): FederalTaxConfig => {
  if (input.accountType === AccountTypes.USA_RT_IRA) {
    return {
      defaultTaxWithholding: 0,
      canChangeTaxWithholding: true,
    };
  }

  return {
    defaultTaxWithholding: 10,
    canChangeTaxWithholding: true,
  };
};

const buildStateTaxConfig = (input: TaxConfigInput): StateTaxConfigs => ({
  // #region Mandatory States
  ME: {
    minTaxWithholding: 5,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatory', {
        state: 'ME',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  MA: {
    minTaxWithholding: 5,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatory', {
        state: 'MA',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  NE: {
    minTaxWithholding: 5,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatory', {
        state: 'NE',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  VT: {
    minTaxWithholding: 0.3, // 30% of federal tax
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0
        ? Number((federalTaxInput * this.minTaxWithholding).toFixed(2))
        : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      const minTaxAmount = Number(
        (federalTaxInput * this.minTaxWithholding).toFixed(2),
      );

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= minTaxAmount &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.stateSpecific.VT', {
        state: 'VT',
        minTaxWithholding: this.minTaxWithholding * 100,
      });
    },
    inlineErrorMessage(federalTaxInput: number) {
      const taxAmount = Number(
        (federalTaxInput * this.minTaxWithholding).toFixed(2),
      );
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: taxAmount,
      });
    },
  },
  // #endregion

  // #region Mandatory Opt Out States
  MI: {
    minTaxWithholding: 4.25,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatoryOptOut', {
        state: 'MI',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  AR: {
    minTaxWithholding: 3,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatoryOptOut', {
        state: 'AR',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  CT: {
    minTaxWithholding: 6.99,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatoryOptOut', {
        state: 'CT',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  MN: {
    minTaxWithholding: 6.25,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatoryOptOut', {
        state: 'MN',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  NC: {
    minTaxWithholding: 4,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatoryOptOut', {
        state: 'NC',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  OK: {
    minTaxWithholding: 4.75,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatoryOptOut', {
        state: 'OK',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  OR: {
    minTaxWithholding: 8,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatoryOptOut', {
        state: 'OR',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  IA: {
    minTaxWithholding: 3.8,
    defaultTaxWithholding(federalTaxInput: number) {
      return federalTaxInput > 0 ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput == 0 && federalTaxInput == 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.mandatoryOptOut', {
        state: 'IA',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
    inlineErrorMessage() {
      return t('components:stateTaxMessages.validation.minTaxWithholding', {
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  // #endregion

  // #region Voluntary States (no minimum tax withholding)
  AL: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'AL' }),
  },
  AZ: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'AZ' }),
  },
  DE: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'DE' }),
  },
  IN: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'IN' }),
  },
  KS: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'KS' }),
  },
  LA: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'LA' }),
  },
  MD: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'MD' }),
  },
  MS: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => {
      if (input.accountType === AccountTypes.USA_RT_IRA) {
        return false;
      }

      return true;
    },
    stateTaxInfoMessage: () => {
      if (input.accountType === AccountTypes.USA_RT_IRA) {
        return t('components:stateTaxMessages.info.stateSpecific.MS', {
          state: 'MS',
        });
      }

      return t('components:stateTaxMessages.info.voluntary', { state: 'MS' });
    },
  },
  MT: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'MT' }),
  },
  ND: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'ND' }),
  },
  NJ: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'NJ' }),
  },
  NM: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'NM' }),
  },
  OH: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'OH' }),
  },
  PA: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'PA' }),
  },
  RI: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'RI' }),
  },
  SC: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'SC' }),
  },
  VA: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'VA' }),
  },
  WI: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'WI' }),
  },
  WV: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => true,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.voluntary', { state: 'WV' }),
  },
  // #endregion

  // #region Voluntary States with minimum tax withholding
  CA: {
    minTaxWithholding: 0.1, // 10% of federal tax
    defaultTaxWithholding() {
      return 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number, federalTaxInput: number) {
      if (stateTaxInput === 0) return true;

      const minTaxAmount = Number(
        (federalTaxInput * this.minTaxWithholding).toFixed(2),
      );

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= minTaxAmount &&
        stateTaxInput <= 100
      );
    },
    inlineErrorMessage(federalTaxInput: number) {
      const minTaxAmount = Number(
        (federalTaxInput * this.minTaxWithholding).toFixed(2),
      );
      return t(
        'components:stateTaxMessages.validation.optOutOrMinTaxWithholding',
        { optOutTaxWithholding: 0, minTaxWithholding: minTaxAmount },
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.stateSpecific.CA', {
        state: 'CA',
        minTaxWithholding: this.minTaxWithholding * 100,
      });
    },
  },
  CO: {
    minTaxWithholding: 4.4,
    defaultTaxWithholding() {
      return 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number) {
      if (stateTaxInput === 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    inlineErrorMessage() {
      return t(
        'components:stateTaxMessages.validation.optOutOrMinTaxWithholding',
        { optOutTaxWithholding: 0, minTaxWithholding: this.minTaxWithholding },
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.voluntaryWithMinimum', {
        state: 'CO',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  GA: {
    minTaxWithholding: 5.39,
    defaultTaxWithholding() {
      return 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number) {
      if (stateTaxInput === 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    inlineErrorMessage() {
      return t(
        'components:stateTaxMessages.validation.optOutOrMinTaxWithholding',
        { optOutTaxWithholding: 0, minTaxWithholding: this.minTaxWithholding },
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.voluntaryWithMinimum', {
        state: 'GA',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  ID: {
    minTaxWithholding: 5.7,
    defaultTaxWithholding() {
      return 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number) {
      if (stateTaxInput === 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    inlineErrorMessage() {
      return t(
        'components:stateTaxMessages.validation.optOutOrMinTaxWithholding',
        { optOutTaxWithholding: 0, minTaxWithholding: this.minTaxWithholding },
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.voluntaryWithMinimum', {
        state: 'ID',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  IL: {
    minTaxWithholding: 4.95,
    defaultTaxWithholding() {
      return 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number) {
      if (stateTaxInput === 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    inlineErrorMessage() {
      return t(
        'components:stateTaxMessages.validation.optOutOrMinTaxWithholding',
        { optOutTaxWithholding: 0, minTaxWithholding: this.minTaxWithholding },
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.voluntaryWithMinimum', {
        state: 'IL',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  KY: {
    minTaxWithholding: 4,
    defaultTaxWithholding() {
      return 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number) {
      if (stateTaxInput === 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    inlineErrorMessage() {
      return t(
        'components:stateTaxMessages.validation.optOutOrMinTaxWithholding',
        { optOutTaxWithholding: 0, minTaxWithholding: this.minTaxWithholding },
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.voluntaryWithMinimum', {
        state: 'KY',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  MO: {
    minTaxWithholding: 4.7,
    defaultTaxWithholding() {
      return 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number) {
      if (stateTaxInput === 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    inlineErrorMessage() {
      return t(
        'components:stateTaxMessages.validation.optOutOrMinTaxWithholding',
        { optOutTaxWithholding: 0, minTaxWithholding: this.minTaxWithholding },
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.voluntaryWithMinimum', {
        state: 'MO',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  UT: {
    minTaxWithholding: 4.55,
    defaultTaxWithholding() {
      return 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(stateTaxInput: number) {
      if (stateTaxInput === 0) return true;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= this.minTaxWithholding &&
        stateTaxInput <= 100
      );
    },
    inlineErrorMessage() {
      return t(
        'components:stateTaxMessages.validation.optOutOrMinTaxWithholding',
        { optOutTaxWithholding: 0, minTaxWithholding: this.minTaxWithholding },
      );
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.voluntaryWithMinimum', {
        state: 'UT',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  // #endregion

  // #region DC Voluntary OR Mandotory State Tax Structure with minimum
  DC: {
    minTaxWithholding: 10.75,
    defaultTaxWithholding(_federalTaxInput: number, isWithdrawAll: boolean) {
      return isWithdrawAll ? this.minTaxWithholding : 0;
    },
    canChangeTaxWithholding: () => true,
    isValidTaxWithholding(
      stateTaxInput: number,
      _federalTaxInput?: number,
      isWithdrawAll?: boolean,
    ) {
      const taxValue = isWithdrawAll ? this.minTaxWithholding : 0;

      return (
        isNumber(stateTaxInput) &&
        stateTaxInput >= taxValue &&
        stateTaxInput <= 100
      );
    },
    inlineErrorMessage(_federalTaxInput: number, isWithdrawAll?: boolean) {
      return isWithdrawAll
        ? t('components:stateTaxMessages.validation.minTaxWithholding', {
            minTaxWithholding: this.minTaxWithholding,
          })
        : '';
    },
    stateTaxInfoMessage() {
      return t('components:stateTaxMessages.info.stateSpecific.DC', {
        state: 'DC',
        minTaxWithholding: this.minTaxWithholding,
      });
    },
  },
  // #endregion

  // #region No Election States
  AK: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'AK' }),
  },
  FL: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'FL' }),
  },
  HI: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'HI' }),
  },
  NH: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'NH' }),
  },
  NV: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'NV' }),
  },
  NY: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'NY' }),
  },
  SD: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'SD' }),
  },
  TN: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'TN' }),
  },
  TX: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'TX' }),
  },
  WA: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'WA' }),
  },
  WY: {
    minTaxWithholding: 0,
    defaultTaxWithholding: () => 0,
    canChangeTaxWithholding: () => false,
    stateTaxInfoMessage: () =>
      t('components:stateTaxMessages.info.noElection', { state: 'WY' }),
  },
  // #endregion
});

export const TaxConfig = (input: TaxConfigInput): TaxConfigResponse => ({
  federalTaxConfig: buildFederalTaxConfig(input),
  stateTaxConfig: buildStateTaxConfig(input),
});
